import React, { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { T, statusColor, statusFill, GOLD_INK } from "../theme/tokens.js";
import { Panel, Empty, Btn, Chip, Notice, Label, Labelled, Kpi, Modal } from "../components/ui/index.jsx";
import TrackerChart from "../components/TrackerChart.jsx";
import DrillPanel from "../components/DrillPanel.jsx";
import SiteMap from "../components/SiteMap.jsx";
import RowTable from "../components/RowTable.jsx";
import ColumnsEditor from "../components/ColumnsEditor.jsx";
import LiveSource from "../components/LiveSource.jsx";
import { findCoordinates } from "../lib/geo.js";
import { useTrackers, useTracker } from "../lib/useTrackers.js";
import {
  columnShapes, columnProblems, COLUMN_TYPES, isCategory,
  AGGREGATIONS, CHART_KINDS, suggestView, describeView,
} from "../lib/trackerViews.js";
import { keyFor } from "../lib/importers/spreadsheet.js";
import { QuickPick, InlineEdit } from "../components/CellEdit.jsx";
import { fmtDate } from "../lib/format.js";

const input = {
  padding: "6px 9px", border: `1px solid ${T.line}`, borderRadius: 4,
  fontSize: 13, background: "#fff", color: T.ink,
};

function Field({ label, children }) {
  return (
    <div style={{ display: "grid", gap: 4, minWidth: 150 }}>
      <Label color={T.navy}>{label}</Label>
      {children}
    </div>
  );
}

/* Building a view is four choices, and the fourth only appears when it can do
   anything. Everything is a dropdown over this tracker's own columns, so there
   is nothing to learn and nothing to type wrong. */
function ViewBuilder({ columns, rows, initial, onSave, onCancel, onDrill }) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [spec, setSpec] = useState(initial?.spec ?? suggestView(columns, rows) ?? {
    kind: "bar", measure: { agg: "count" },
  });

  const set = (patch) => setSpec((s) => ({ ...s, ...patch }));
  const groupable = columns.filter((c) => c.type !== "number");
  const numeric = columns.filter((c) => c.type === "number");
  const agg = AGGREGATIONS.find((a) => a.key === (spec.measure?.agg ?? "count"));
  const groupCol = columns.find((c) => c.key === spec.groupBy);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
        <Field label="Draw it as">
          <select style={input} value={spec.kind}
            onChange={(e) => set({ kind: e.target.value })}>
            {CHART_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        </Field>

        <Field label="Measure">
          <select style={input} value={spec.measure?.agg ?? "count"}
            onChange={(e) => set({ measure: { ...spec.measure, agg: e.target.value } })}>
            {AGGREGATIONS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
        </Field>

        {agg?.needsColumn && (
          <Field label="Of which column">
            <select style={input} value={spec.measure?.column ?? ""}
              onChange={(e) => set({ measure: { ...spec.measure, column: e.target.value } })}>
              <option value="">Pick a number column</option>
              {numeric.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
        )}

        {spec.kind !== "kpi" && (
          <Field label="Grouped by">
            <select style={input} value={spec.groupBy ?? ""}
              onChange={(e) => set({ groupBy: e.target.value })}>
              <option value="">Pick a column</option>
              {groupable.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
        )}

        {spec.kind !== "kpi" && groupCol?.type === "date" && (
          <Field label="By">
            <select style={input} value={spec.bucket ?? "month"}
              onChange={(e) => set({ bucket: e.target.value })}>
              <option value="day">Day</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Year</option>
            </select>
          </Field>
        )}

        {spec.kind !== "kpi" && spec.kind !== "donut" && (
          <Field label="Split into">
            <select style={input} value={spec.splitBy ?? ""}
              onChange={(e) => set({ splitBy: e.target.value || undefined })}>
              <option value="">One series</option>
              {groupable.filter((c) => c.key !== spec.groupBy)
                .map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
        )}
      </div>

      <div style={{ fontSize: 12, color: T.soft }}>
        {CHART_KINDS.find((k) => k.key === spec.kind)?.note}
      </div>

      <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, padding: 12 }}>
        {/* The preview opens too. Clicking a bar while you are still choosing
            the shape is the fastest way to find out whether the grouping is
            the one you meant. */}
        <TrackerChart spec={spec} columns={columns} rows={rows} onDrill={onDrill} />
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
        <Field label="Call it">
          <input style={{ ...input, width: 280 }} value={title}
            placeholder={describeView(spec, columns)}
            onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Btn kind="gold"
          onClick={() => onSave({
            id: initial?.id,
            title: title.trim() || describeView(spec, columns),
            spec,
          })}>
          {initial?.id ? "Save changes" : "Save this view"}
        </Btn>
        <Btn kind="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
}

export function NewTracker({ onClose, onCreate }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [columns, setColumns] = useState([
    { key: "item", label: "", type: "text" },
    { key: "status", label: "", type: "choice", options: ["Open", "In progress", "Complete"] },
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    const problem = !title.trim() ? "Give the tracker a name." : columnProblems(columns);
    if (problem) { setError(problem); return; }
    setBusy(true);
    const ok = await onCreate({
      title, description,
      columns: columns.map((c) => ({ ...c, label: c.label.trim() })),
    });
    setBusy(false);
    if (!ok) setError("That could not be saved. Try again.");
  };

  return (
    <Modal title="Start a tracker" onClose={onClose} wide>
      <div style={{ display: "grid", gap: 14 }}>
        <Labelled label="Name"
          hint="What you would call it in a meeting. WAPA Pole Installation, Outage Window Requests.">
          <input autoFocus style={input} value={title}
            onChange={(e) => setTitle(e.target.value)} />
        </Labelled>

        <Labelled label="What it is for"
          hint="Optional. A line for whoever opens this in six months.">
          <textarea style={{ ...input, minHeight: 54, resize: "vertical" }} value={description}
            onChange={(e) => setDescription(e.target.value)} />
        </Labelled>

        <div>
          <Label color={T.navy}>The columns</Label>
          <div style={{ fontSize: 12, color: T.soft, margin: "4px 0 10px" }}>
            One per thing you need to record. You can add, rename and reorder them
            later without losing a single row.
          </div>
          <ColumnsEditor columns={columns} onChange={setColumns} />
        </div>
      </div>

      {error && <div style={{ marginTop: 12 }}><Notice kind="error">{error}</Notice></div>}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="gold" disabled={busy} onClick={submit}>
          {busy ? "Creating..." : "Create it"}
        </Btn>
      </div>
    </Modal>
  );
}

/* The rows, edited where they sit.
 *
 * Every cell writes the moment you leave it. There is no Save on this table
 * and no separate edit mode, because the point of it is a tracker that keeps
 * up with a call rather than one somebody transcribes afterwards.
 *
 * An imported tracker edits the same way as one made here. The workbook is
 * where it came from, not where it lives, and the one place that distinction
 * still matters is a re-import, which replaces every row and is told to say so.
 */


function TrackerDetail({ trackerId, onBack }) {
  const {
    tracker, rows, views, error, saveView, removeView, rename,
    setCell, addRow, removeRow, setColumns,
  } = useTracker(trackerId);
  const [building, setBuilding] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [drill, setDrill] = useState(null);
  const [editingColumns, setEditingColumns] = useState(null);

  if (!tracker || !rows) return <Empty text="Loading..." />;
  const columns = tracker.columns ?? [];

  /* A tracker has no fixed shape, so the drill panel is handed this
     workbook's own columns rather than a register's. */
  /* What is already in a column, for turning it into a list of choices. Capped,
     because a column of 125 device ids is not a dropdown. */
  const distinctValues = (key) => {
    const seen = new Set();
    for (const r of rows) {
      const v = r[key];
      if (v === null || v === undefined || v === "") continue;
      seen.add(String(v));
      if (seen.size > 20) return [];
    }
    return [...seen].sort();
  };

  const drillColumns = columns.map((c) => [
    c.label,
    (r) => (c.type === "date" ? fmtDate(r[c.key]) : r[c.key] ?? ""),
  ]);

  return (
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      {error && <Notice kind="error">{error}</Notice>}

      <Panel title={tracker.title}
        right={
          <div style={{ display: "flex", gap: 8 }}>
            <Btn small kind="ghost"
              onClick={() => setRenaming({ title: tracker.title,
                                           description: tracker.description ?? "" })}>
              Rename
            </Btn>
            <Btn small kind="ghost" onClick={() => setEditingColumns(columns)}>Columns</Btn>
            <Btn small kind="ghost" onClick={onBack}>
              <ArrowLeft size={13} style={{ verticalAlign: "-2px" }} /> All trackers
            </Btn>
          </div>
        }>
        <div style={{ display: "grid", gap: 12,
                      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
          <Kpi label="Rows" value={rows.length.toLocaleString()} />
          <Kpi label="Columns" value={columns.length} color={T.steel} />
          <Kpi label="Saved views" value={views?.length ?? 0} color={GOLD_INK} />
          <Kpi label="Last updated" value={fmtDate(tracker.updated_at?.slice(0, 10))}
            sub={tracker.source_sheet ? `${tracker.source_file} · ${tracker.source_sheet}` : ""} />
        </div>
        {tracker.description && (
          <div style={{ fontSize: 13, color: T.soft, marginTop: 12 }}>{tracker.description}</div>
        )}
      </Panel>

      {/* A tracker that records where things are gets a map before it gets a
          chart, because on this program the first question is which island
          and how far across it the crews have got. */}
      {findCoordinates(columns, rows) && (
        <SiteMap columns={columns} rows={rows}
          title={`${tracker.title}: where the work is`}
          onDrill={(d) => setDrill({ ...d, columns: drillColumns })} />
      )}

      {(views ?? []).map((v) => (
        <Panel key={v.id} title={v.title} accent={T.gold}
          right={
            <div style={{ display: "flex", gap: 8 }}>
              <Btn small kind="ghost" onClick={() => setBuilding(v)}>Edit</Btn>
              <Btn small kind="danger" onClick={() => setConfirmRemove(v)}>Remove</Btn>
            </div>
          }>
          <TrackerChart spec={v.spec} columns={columns} rows={rows}
            onDrill={(d) => setDrill({ ...d, columns: drillColumns })} />
        </Panel>
      ))}

      {drill && <DrillPanel drill={drill} onClose={() => setDrill(null)} />}

      {building ? (
        <Panel title={building.id ? "Edit this view" : "New view"} accent={T.steel}>
          <ViewBuilder columns={columns} rows={rows} initial={building.id ? building : null}
            onSave={async (v) => { if (await saveView(v)) setBuilding(null); }}
            onDrill={(d) => setDrill({ ...d, columns: drillColumns })}
            onCancel={() => setBuilding(null)} />
        </Panel>
      ) : (
        <div>
          <Btn kind="gold" onClick={() => setBuilding({})}>Add a view</Btn>
          {(views?.length ?? 0) === 0 && (
            <span style={{ fontSize: 12.5, color: T.soft, marginLeft: 12 }}>
              Set a chart up once and it is here every month.
            </span>
          )}
        </div>
      )}

      <Panel title="The rows themselves">
        <RowTable columns={columns} rows={rows}
          onEdit={setCell} onAdd={addRow} onRemove={removeRow} />
      </Panel>

      {renaming && (
        <Modal title="Name this tracker" onClose={() => setRenaming(null)}>
          <div style={{ display: "grid", gap: 12 }}>
            <Labelled label="Name"
              hint="What you would call this in a meeting. It came in as the sheet name.">
              <input autoFocus style={input} value={renaming.title}
                onChange={(e) => setRenaming({ ...renaming, title: e.target.value })} />
            </Labelled>
            <Labelled label="What it is for"
              hint="Optional. A line for whoever opens this in six months.">
              <textarea style={{ ...input, minHeight: 60, resize: "vertical" }}
                value={renaming.description}
                onChange={(e) => setRenaming({ ...renaming, description: e.target.value })} />
            </Labelled>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn kind="ghost" onClick={() => setRenaming(null)}>Cancel</Btn>
            <Btn disabled={!renaming.title.trim()} onClick={async () => {
              if (await rename(renaming.title, renaming.description || null)) setRenaming(null);
            }}>Save</Btn>
          </div>
        </Modal>
      )}

      {editingColumns && (
        <Modal title="The columns" onClose={() => setEditingColumns(null)} wide>
          <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 12, lineHeight: 1.6 }}>
            Renaming a column changes what it says, never where its data lives, so
            nothing is lost. Removing one takes it off the table; the values stay in
            the database and come back if you add the column again.
          </div>
          <ColumnsEditor columns={editingColumns} onChange={setEditingColumns}
            suggestOptions={distinctValues} />
          {columnProblems(editingColumns) && (
            <div style={{ marginTop: 12 }}>
              <Notice kind="error">{columnProblems(editingColumns)}</Notice>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn kind="ghost" onClick={() => setEditingColumns(null)}>Cancel</Btn>
            <Btn disabled={Boolean(columnProblems(editingColumns))} onClick={async () => {
              if (await setColumns(editingColumns.map((c) => ({ ...c, label: c.label.trim() })))) {
                setEditingColumns(null);
              }
            }}>Save</Btn>
          </div>
        </Modal>
      )}

      {confirmRemove && (
        <Modal title="Remove this view?" onClose={() => setConfirmRemove(null)}>
          <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.6, marginBottom: 16 }}>
            {confirmRemove.title} goes away. The tracker and its rows are untouched,
            and you can build the same view again in about ten seconds.
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn kind="ghost" onClick={() => setConfirmRemove(null)}>Keep it</Btn>
            <Btn kind="danger" onClick={async () => {
              await removeView(confirmRemove.id); setConfirmRemove(null);
            }}>Remove</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function Trackers({ contract, onGoToImport, onReloadContracts }) {
  const { trackers, error, create } = useTrackers(contract?.id);
  const [open, setOpen] = useState(null);
  const [live, setLive] = useState(null);
  const [starting, setStarting] = useState(false);

  /* Layers the client keeps and this contract reads. Set on the contract, in
     its settings, because which layer belongs to which program is a fact
     about the contract and not about any one person's screen. */
  const sources = Array.isArray(contract?.map_sources) ? contract.map_sources : [];
  const openSource = sources.find((s) => s.id === live) ?? null;

  if (openSource) {
    return (
      <LiveSource source={openSource} contract={contract}
        onBack={() => setLive(null)} onSourceChange={onReloadContracts} />
    );
  }
  if (open) return <TrackerDetail trackerId={open} onBack={() => setOpen(null)} />;
  if (trackers === null) return <Empty text="Loading..." />;

  return (
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      {error && <Notice kind="error">{error}</Notice>}

      <Panel title={`Trackers · ${contract?.name ?? ""}`}
        right={
          <div style={{ display: "flex", gap: 8 }}>
            <Btn small kind="gold" onClick={() => setStarting(true)}>Start one from scratch</Btn>
            {onGoToImport && (
              <Btn small kind="ghost" onClick={onGoToImport}>From a workbook</Btn>
            )}
          </div>
        }>
        {sources.length > 0 && (
          <div style={{ display: "grid", gap: 10, marginBottom: trackers.length ? 18 : 0 }}>
            {sources.map((src) => (
              <button key={src.id} onClick={() => setLive(src.id)} style={{
                textAlign: "left", border: `1px solid ${T.line}`, borderRadius: 5,
                background: T.panel, padding: "12px 14px", cursor: "pointer",
                display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center",
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: T.navy, fontSize: 14 }}>{src.title}</div>
                  <div style={{ fontSize: 12.5, color: T.soft, marginTop: 3,
                                overflow: "hidden", textOverflow: "ellipsis",
                                whiteSpace: "nowrap" }}>
                    {src.description || src.url}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", whiteSpace: "nowrap" }}>
                  <Chip color={GOLD_INK}>Live from the client</Chip>
                </div>
              </button>
            ))}
          </div>
        )}

        {trackers.length === 0 && sources.length === 0 ? (
          <Empty text="No trackers yet. Start one from scratch and name its columns, or build the workbook as you always would and drop it into Import, where its own headers become the columns." />
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {trackers.map((t) => (
              <button key={t.id} onClick={() => setOpen(t.id)} style={{
                textAlign: "left", border: `1px solid ${T.line}`, borderRadius: 5,
                background: T.panel, padding: "12px 14px", cursor: "pointer",
                display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center",
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: T.navy, fontSize: 14 }}>{t.title}</div>
                  <div style={{ fontSize: 12.5, color: T.soft, marginTop: 3 }}>
                    {(t.columns ?? []).map((c) => c.label).slice(0, 6).join(", ")}
                    {(t.columns ?? []).length > 6 ? ` and ${t.columns.length - 6} more` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", whiteSpace: "nowrap" }}>
                  {/* Where it came from, because a re-import replaces every row of
                      one of these and does nothing to the other. */}
                  <Chip color={T.soft}>
                    {t.source_file ? "From a workbook" : "Made here"}
                  </Chip>
                  <Chip color={T.steel}>{t.row_count.toLocaleString()} rows</Chip>
                </div>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {starting && (
        <NewTracker onClose={() => setStarting(false)}
          onCreate={async (t) => {
            const id = await create(t);
            if (id) { setStarting(false); setOpen(id); }
            return Boolean(id);
          }} />
      )}
    </div>
  );
}
